import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { tenantSettingsService } from '../services/tenantSettingsService'
import type { TenantSettings } from '../types/tenantSettings'
import './SettingsStc.css'

export function SettingsStc() {
  const queryClient = useQueryClient()
  const [stcWacomLicenceId, setStcWacomLicenceId] = useState('')
  const [stcWacomLicenceKey, setStcWacomLicenceKey] = useState('')
  const [showWacomKey, setShowWacomKey] = useState(false)
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const { data: tenantSettings, isLoading } = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: () => tenantSettingsService.getCurrent(),
  })

  const updateMutation = useMutation({
    mutationFn: (data: Partial<TenantSettings>) => tenantSettingsService.update(data),
    onSuccess: (updated) => {
      queryClient.setQueryData(['tenant-settings'], updated)
      setSaveMessage({ type: 'success', text: 'STC settings saved successfully.' })
      setTimeout(() => setSaveMessage(null), 3000)
    },
    onError: (error: Error) => {
      setSaveMessage({ type: 'error', text: error.message || 'Failed to save settings' })
    },
  })

  useEffect(() => {
    if (tenantSettings) {
      setStcWacomLicenceId(tenantSettings.stcWacomLicenceId ?? '')
      setStcWacomLicenceKey(tenantSettings.stcWacomLicenceKey ?? '')
    }
  }, [tenantSettings])

  const handleSave = () => {
    setSaveMessage(null)
    const licenceId = stcWacomLicenceId.trim() || null
    const licenceKey = stcWacomLicenceKey.trim() || null
    if (typeof sessionStorage !== 'undefined') {
      if (!licenceId) {
        sessionStorage.removeItem('clnicl_stc_wacom_licence_id')
        sessionStorage.removeItem('clnicl_stc_wacom_licence_key')
      } else {
        sessionStorage.setItem('clnicl_stc_wacom_licence_id', licenceId)
        if (licenceKey) {
          sessionStorage.setItem('clnicl_stc_wacom_licence_key', licenceKey)
        }
      }
    }
    updateMutation.mutate({
      sessionTimeoutMinutes: tenantSettings?.sessionTimeoutMinutes ?? 30,
      stcWacomLicenceId: licenceId,
      stcWacomLicenceKey: licenceKey,
    })
  }

  if (isLoading) {
    return (
      <div className="settings-stc">
        <p>Loading settings…</p>
      </div>
    )
  }

  return (
    <div className="settings-stc">
      <div className="settings-stc-header">
        <h2>STC &amp; PCRS</h2>
        <p className="settings-stc-subtitle">
          Configure Wacom signature pad licence settings for STC and MICS signing.
        </p>
      </div>

      <div className="settings-stc-content">
        <section className="settings-stc-section" aria-labelledby="stc-wacom-heading">
          <h3 id="stc-wacom-heading">Wacom signature pad (optional)</h3>
          <p className="form-hint">
            Licence for Wacom SigCaptX SDK used for signature capture on a Wacom STU device. Leave blank if not using a signature pad.
          </p>
          <div className="form-group">
            <label htmlFor="stc-wacom-licence-id">Licence ID</label>
            <input
              id="stc-wacom-licence-id"
              type="text"
              value={stcWacomLicenceId}
              onChange={(e) => setStcWacomLicenceId(e.target.value)}
              placeholder="UUID from Wacom"
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label htmlFor="stc-wacom-licence-key">Licence key</label>
            <div className="api-key-input-wrapper">
              <input
                id="stc-wacom-licence-key"
                type={showWacomKey ? 'text' : 'password'}
                value={stcWacomLicenceKey}
                onChange={(e) => setStcWacomLicenceKey(e.target.value)}
                placeholder={tenantSettings?.hasStcWacomLicence ? '••••••••' : 'Enter key'}
                className="form-control"
                aria-describedby="stc-wacom-key-hint"
              />
              <button
                type="button"
                className="toggle-api-key-btn"
                onClick={() => setShowWacomKey(!showWacomKey)}
                title={showWacomKey ? 'Hide key' : 'Show key'}
                aria-label={showWacomKey ? 'Hide licence key' : 'Show licence key'}
              >
                {showWacomKey ? '🙈' : '👁️'}
              </button>
            </div>
            <p id="stc-wacom-key-hint" className="form-hint">
              Use the eye icon to show or hide the key.
            </p>
          </div>
        </section>

        {saveMessage && (
          <div className={`save-message ${saveMessage.type}`} role="alert">
            {saveMessage.text}
          </div>
        )}

        <div className="settings-stc-actions">
          <button
            type="button"
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="btn-primary"
          >
            {updateMutation.isPending ? 'Saving…' : 'Save STC settings'}
          </button>
        </div>
      </div>
    </div>
  )
}
